import React, { useContext, useEffect, useReducer, useState, useTransition } from 'react';
import { isServer, settings } from '../../settings';
import { useExternalScript } from '../common/hooks';
import { documentGetCookie, documentSetCookie } from '../../cookie';
import { compositeKey, removeKey } from '../common/o';
import { PreferenceContext, RequestContext, UserContext } from '../context';
import { useLocation } from 'react-router-dom';

const getIABConsentString = () => {
  if (typeof window !== 'object') {
    return '';
  }
  if (window.didomiState && window.didomiState.didomiIABConsent) {
    return window.didomiState.didomiIABConsent;
  }
  const cookieName = 'euconsent-v2';
  let item = documentGetCookie(cookieName);
  if (item) {
    return item;
  }
  try {
    item = window.localStorage.getItem(cookieName);
  } catch (e) {
    /* no-op */
  }

  return item;
};

const brands = settings.PLAYER_BRANDS.split(',').map((x) => x.trim());
export const getPlayerBrand = () => {
  if (isServer) {
    return;
  }
  const cookieName = 'player_brand';
  let brand = documentGetCookie(cookieName);
  if (!brands.includes(brand)) {
    brand = brands[Math.floor(Math.random() * brands.length)];
    documentSetCookie(cookieName, brand);
  }
  return brand;
};

export const AdsContext = React.createContext({});
AdsContext.displayName = 'Ads';

const getDurationType = (duration) =>
  [
    [0, 30, 'clip'],
    [30, 60, 'short'],
    [60, 180, 'medium'],
    [180, 300, 'long'],
    [300, Number.MAX_VALUE, 'extra long'],
  ].reduce((a, [from, to, type]) => (duration > from && duration <= to && !a ? type : a), null) ||
  'unknown';

const KaChingSDK = ({ onReady }) => {
  const { user } = useContext(UserContext);
  const attributes = user ? { 'data-uid': user.hashedId } : {};
  const kachingScript = useExternalScript(settings.ADS_KACHING_SCRIPT, attributes);

  const [kaching, setKaching] = useState(null);

  useEffect(() => {
    if (![kachingScript].includes('loading') && !kaching) {
      if (kachingScript === 'error') {
        setKaching({ error: 'failed to load ka-ching script' });
      } else {
        try {
          const k = new window.KaChing();
          setKaching(k);
        } catch (e) {
          setKaching({ error: e });
        }
      }
    } else if (kaching) {
      onReady(kaching);
    }
  }, [kachingScript, kaching]);
  return null;
};

const DigitalAudienceSDK = () => {
  const daScript = useExternalScript('https://target.digitalaudience.io/bakery/scripts/da.js');
  const { user } = useContext(UserContext);
  const { pathname, search } = useLocation();
  const email = (user && user.hashedEmail) || '';

  useEffect(() => {
    if (daScript === 'ready' && window.__da) {
      try {
        const parcid = window.cX?.getCxenseUserId();
        window.__da.bake({
          publisher: 'dap_241126',
          syncs: [],
          dpixel: '2617',
          partner: 'po_241126',
          parcid,
        });
      } catch (e) {
        // call sales team for error logging
      }
    }
  }, [daScript, email, pathname + search]);
};

const adunitFullPath = (a) =>
  `dumpert.nl/${settings.ADS_USE_PREVIEW_AD_UNIT ? 'preview/' : ''}${a}`;
const videoTargeting = (video) => ({
  iabconsentstring: getIABConsentString(),
  ...(video
    ? {
        vid_duration: String(video.duration),
        vid_duration_type: getDurationType(video.duration),
      }
    : {}),
});
const adsConfigReducer = (state, { type, payload }) => {
  switch (type) {
    case 'homepage':
    case 'toppers':
    case 'dumperttv':
      return {
        ...state,
        adunit: adunitFullPath(payload.adunit),
        adunitHash: undefined,
        blockads: false,
        video: false,
        targeting: {
          ...state.targeting,
          page_type: type === 'homepage' ? 'home' : type,
          article_taglist: [],
        },
        videoTargeting: videoTargeting(),
      };
    case 'playlist':
    case 'item':
      // eslint-disable-next-line no-case-declarations
      const { id, nopreroll, nsfw, media = [], tags = '', title } = payload || {};

      if (state && state.adunitHash) {
        const prevHash = state.adunitHash.split('-');
        if (
          prevHash.length === 2 &&
          prevHash[0] === id &&
          prevHash[1] !== '0' &&
          media.length === 0
        ) {
          // item already has been loaded
          return state;
        }
      }

      // eslint-disable-next-line no-case-declarations
      const video = media.find((x) => x.mediatype === 'VIDEO');
      return {
        ...state,
        adunitHash: `${id}-${media.length}`,
        adunit: adunitFullPath('detail'),
        blockads: !media.length || !!nopreroll || !!nsfw,
        video: !!video,
        targeting: {
          ...state.targeting,
          page_type: type === 'item' ? 'detail' : type,
          article_id: id,
          article_title: title,
          article_taglist: tags ? tags.split(' ') : [],
        },
        videoTargeting: videoTargeting(video),
      };
    default:
      return state;
  }
};

const slotKey = (slot) => compositeKey(['adType', 'adNumber', 'isModalOverlay'], slot || {});
const adsSlotsReducer = (state, { type, payload }) => {
  const key = slotKey(payload);
  switch (type) {
    case 'define':
      if (state.destroy[key]) {
        return {
          ...state,
          destroy: removeKey(key, state.destroy),
        };
      }
      if (state.active[key]) {
        return state;
      }
      return {
        ...state,
        define: { ...state.define, [key]: payload },
      };
    case 'destroy':
      if (state.define[key]) {
        return {
          ...state,
          define: removeKey(key, state.define),
        };
      }
      if (!state.active[key]) {
        return state;
      }
      return {
        ...state,
        destroy: { ...state.destroy, [key]: payload },
      };
    case 'setup':
      return {
        define: {},
        destroy: {},
        active: { ...payload.active },
        page: payload.page,
      };
    default:
      return state;
  }
};

let updateTimeout = null;
export const AdsContextProvider = ({ children }) => {
  const [kaching, setKaching] = useState(null);
  const [kachingInitialized, setKachingInitialized] = useState(false);
  const { userAgent } = useContext(RequestContext);
  const { user } = useContext(UserContext);
  const { preference = {} } = useContext(PreferenceContext);
  const setTransition = useTransition()[1];

  const [kachingConfig, dispatchKachingConfig] = useReducer(adsConfigReducer, {}, () => ({
    adunit: adunitFullPath('homepage'),
    blockads: false,
    targeting: {
      browser_name: userAgent ? userAgent.browser.name : '',
      playerbrand: getPlayerBrand(),
    },
    lazyload: {
      fetchMarginPercent: userAgent && userAgent.isDesktop ? 50 : 25,
      renderMarginPercent: userAgent && userAgent.isDesktop ? 25 : 10,
      scatterSlots: true,
    },
  }));
  const [kachingSlots, dispatchKachingSlots] = useReducer(adsSlotsReducer, {}, () => ({
    define: {},
    destroy: {},
    active: {},
  }));

  useEffect(() => {
    if (!kaching) {
      // waiting for script to be loaded
      return;
    }

    if (kaching.error) {
      // script is failed to load, ensure player is not blocked to show video
      setKachingInitialized(true);
      return;
    }

    // spawn new timeout to apply new configuration asynchronously
    // if previous timeout is not finished yet discard it.
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }

    // ensure player does not start showing preroll until actual ads configuration is applied,
    // finally, must set back to true in any case to let player start playing video.
    setKachingInitialized(false);

    /*
    SB-1313 ka-ching api calls

      when the user visits relevant urls adunit is changed to
        homepage, toppers, dumperttv (type of items list page)
        and detail (item page)

      every time adunit is changed (or item is changed for adunit ‘detail’)
        kaching.reset is called and then kaching.defineSlot and then kaching.init,

      when new ad should be added but  kaching.reset is not called because of adunit or detail is the same
        (for instance between items in the list when scroll down)
        then kaching.defineSlot is called and then kaching.init
     */

    updateTimeout = setTimeout(() => {
      setTransition(() => {
        const { destroy, define, active, page } = kachingSlots;
        const { adunit, adunitHash = '*' } = kachingConfig;
        let newActive = { ...active };
        const pageDefined = adunit !== undefined;
        const destroySlots = Object.entries(destroy);
        const defineSlots = Object.entries(define);
        const pageConfig = pageDefined ? `${adunit}-${adunitHash}` : undefined;
        const pageChanged = page !== pageConfig;
        const slotsChanged = defineSlots.length + destroySlots.length > 0;
        const doReset = page !== undefined && pageChanged;

        for (const [key, { adType, adNumber }] of destroySlots) {
          kaching.destroy(`ad_${adType}-${adNumber}`);
          newActive = removeKey(key, newActive);
        }

        for (const [key, slot] of defineSlots) {
          const { adType, adNumber } = slot;
          if (!doReset) {
            kaching.defineSlot(adType, adNumber);
          }

          newActive = { ...newActive, [key]: slot };
        }

        if (slotsChanged || pageChanged) {
          const setup = () =>
            dispatchKachingSlots({
              type: 'setup',
              payload: { active: newActive, page: pageConfig },
            });
          if (doReset) {
            kaching
              .reset()
              .then(() => {
                for (const { adType, adNumber } of Object.values(newActive)) {
                  kaching.defineSlot(adType, adNumber);
                }
              })
              .finally(setup);
          } else {
            setup();
          }
        } else if (pageDefined) {
          try {
            kaching
              .init({
                ...kachingConfig,
                targeting: {
                  ...kachingConfig.targeting,
                  logged_in: String(!!user),
                  view_mode: String(preference.landscape),
                  window_width: String(Math.round(window.innerWidth / 50) * 50),
                },
              })
              .finally(() => {
                try {
                  setKachingInitialized(true);
                } catch (e) {
                  /* no-op */
                }
              });
          } catch (e) {
            try {
              setKachingInitialized(true);
            } catch (e) {
              /* no-op */
            }
          }
          setTimeout(() => {
            try {
              setKachingInitialized(true);
            } catch (e) {
              /* no-ops */
            }
          }, 2500);
        }
      });
    }, 300);
  }, [
    kaching,
    JSON.stringify(kachingSlots),
    JSON.stringify(kachingConfig),
    !!user,
    preference.landscape,
  ]);

  return (
    <>
      <AdsContext.Provider
        value={{ dispatchKachingConfig, dispatchKachingSlots, kaching, kachingInitialized }}
      >
        {children}
      </AdsContext.Provider>
      <KaChingSDK onReady={setKaching} />
      <DigitalAudienceSDK />
    </>
  );
};
